// ============================================================================
// Adaptateur « moteur actuel → noyau » (lot 3 de la refonte,
// docs/moteur/REFONTE_MOTEUR.md). Lecture seule : rien ici ne juge, rien n'est
// branché à l'app.
//
//   - `readLegacyLine` : une ligne de feuille (texte, lu par le parseur PARTAGÉ)
//     → énoncés du noyau. Une chaîne « Δ = 25 − 24 = 1 » devient une suite
//     d'énoncés « Δ = 25 − 24 », « Δ = 1 » (chaque maillon se juge depuis le
//     précédent, comme deux lignes) ; ce que le noyau ne lit pas est nommé
//     (« ≈ », congruence, nœud inconnu) pour la feuille de route.
//   - `legacySheetFacts` : ce que le modèle d'une feuille dit d'emblée —
//     valeurs EXACTES des grandeurs données, hypothèses de signe (longueur > 0,
//     rang entier), égalités de l'énoncé et résultats admis.
//   - `headOf`, `withKnown` : la grandeur qu'une ligne définit (« S = … ») et
//     le remplacement des valeurs connues des AUTRES grandeurs.
// ============================================================================

import { parseText, type Line } from '../tree';
import {
  fromNode,
  mapStatement,
  statementOfLine,
  statementSymbols,
  substitute,
  symbolsOf,
  UnsupportedTerm,
  type Statement,
  type Term,
} from './term';
import type { Condition } from './types';

/* ------------------------------ lignes ------------------------------ */

export type LegacyReadFailure =
  | 'unparsable'
  | 'approximation'
  | 'congruence'
  | 'chain_relation'
  | `node:${string}`
  | 'unsupported';

export type LegacyRead =
  | {
      ok: true;
      parts: Statement[];
      /** chaîne « S = a = b » (définition : S = a, S = b) ou « a = b = c » (maillons a = b, b = c) */
      chain: false | 'definition' | 'links';
    }
  | { ok: false; reason: LegacyReadFailure };

/** Ce que le noyau ne sait pas lire dans une ligne, nommé (feuille de route). */
function failureOf(line: Line): LegacyReadFailure {
  for (const eq of line.dis) {
    if (eq.cmp === '≈') return 'approximation';
    if (eq.rels || eq.mod !== undefined) return 'congruence';
    if (eq.cmp && line.dis.length > 1) return 'chain_relation';
    for (const side of eq.sides) {
      try {
        fromNode(side);
      } catch (e) {
        if (e instanceof UnsupportedTerm) return `node:${e.what}`;
        throw e;
      }
    }
  }
  return 'unsupported';
}

/** Texte d'une ligne → énoncés du noyau (lu par le parseur partagé). */
export function readLegacyLine(text: string): LegacyRead {
  let line: Line;
  try {
    line = parseText(text);
  } catch {
    return { ok: false, reason: 'unparsable' };
  }
  return readLegacyParsed(line);
}

/**
 * Ligne DÉJÀ LUE par le moteur actuel (`Line` : ce que son juge a réellement vu) → énoncés
 * du noyau ; une chaîne → un énoncé par maillon. À préférer au texte quand on l'a : le texte
 * imprimé n'est pas toujours relu à l'identique (« (10³)² » s'imprime « 10³² », relu 10^32).
 */
export function readLegacyParsed(line: Line): LegacyRead {
  const one = line.dis.length === 1 ? line.dis[0]! : null;
  // chaîne d'égalités « a = b = c » (pas de comparaison, pas de congruence)
  if (one && one.sides.length > 2 && !one.cmp && !one.rels && one.mod === undefined) {
    try {
      const sides = one.sides.map(fromNode);
      const head = sides[0]!;
      const defines = head.k === 'sym' && sides.slice(1).every((t) => !symbolsOf(t).has(head.n));
      return {
        ok: true,
        chain: defines ? 'definition' : 'links',
        // « S = a = b » : S = a, puis S = b (chaque maillon depuis le précédent) ;
        // sinon « a = b = c » : a = b, b = c (chaque maillon pour lui-même)
        parts: sides.slice(1).map((r, i) => ({
          k: 'rel' as const,
          rel: '=' as const,
          l: defines ? head : sides[i]!,
          r,
        })),
      };
    } catch (e) {
      if (e instanceof UnsupportedTerm) return { ok: false, reason: `node:${e.what}` };
      throw e;
    }
  }
  const s = statementOfLine(line);
  if (s.ok) return { ok: true, parts: [s.s], chain: false };
  return { ok: false, reason: failureOf(line) };
}

/**
 * Valeur approchée « S ≈ v » (lot 3 bis) : les deux membres, lus par le noyau.
 * null si la ligne n'en est pas une (chaîne, plusieurs relations, nœud inconnu).
 */
export function approximationOf(line: Line): { l: Term; r: Term } | null {
  if (line.dis.length !== 1) return null;
  const eq = line.dis[0]!;
  if (eq.cmp !== '≈' || eq.sides.length !== 2 || eq.rels || eq.mod !== undefined) return null;
  try {
    return { l: fromNode(eq.sides[0]!), r: fromNode(eq.sides[1]!) };
  } catch (e) {
    if (e instanceof UnsupportedTerm) return null;
    throw e;
  }
}

/**
 * Grandeur DÉFINIE par la ligne : « S = N(P + D) ÷ 2 » → S, et à l'envers
 * « 6 ÷ 3 = x » → x. Une équation (« 2x + 3 = 7 », « ℓ = 1,2ℓ − 4 », un « ou »)
 * n'en définit pas : null.
 */
export function headOf(s: Statement): string | null {
  if (s.k !== 'rel' || s.rel !== '=') return null;
  if (s.l.k === 'sym' && !symbolsOf(s.r).has(s.l.n)) return s.l.n;
  if (s.r.k === 'sym' && !symbolsOf(s.l).has(s.r.n)) return s.r.n;
  return null;
}

/** Définition « H = e » (H absent de e) : la grandeur et son expression, sinon null. */
export function definitionOf(s: Statement): { name: string; value: Term } | null {
  const h = headOf(s);
  if (!h || s.k !== 'rel') return null;
  return { name: h, value: s.l.k === 'sym' && s.l.n === h ? s.r : s.l };
}

/** Valeur constante (sans lettre) d'une définition « H = 25 », sinon null. */
export function constantDefinition(s: Statement): { name: string; value: Term } | null {
  const d = definitionOf(s);
  return d && symbolsOf(d.value).size === 0 ? d : null;
}

/**
 * Remplace les grandeurs de valeur connue, SAUF celles de `keep` (les lettres que
 * l'élève écrit lui-même dans la ligne jugée : une formule s'écrit en lettres et
 * se juge en lettres, un calcul se juge sur les valeurs).
 */
export function withKnown(
  s: Statement,
  known: ReadonlyMap<string, Term>,
  keep: ReadonlySet<string>,
): Statement {
  // jusqu'à ce que plus rien ne change : E(X) = p puis p = 1/3 (borné : une
  // définition circulaire « a = b, b = a » ne boucle pas)
  let out = s;
  for (let pass = 0; pass < MAX_SUBSTITUTION_PASSES; pass++) {
    const map: Record<string, Term> = {};
    for (const n of statementSymbols(out)) {
      const v = known.get(n);
      if (v && !keep.has(n)) map[n] = v;
    }
    if (!Object.keys(map).length) break;
    out = mapStatement(out, (t) => substitute(t, map));
  }
  return out;
}
const MAX_SUBSTITUTION_PASSES = 6;

/* ------------------------------ le modèle ------------------------------ */

/** Ce que l'adaptateur lit d'un modèle de feuille (sous-ensemble de `ScratchModel`). */
export interface LegacyModelView {
  symbols: Record<
    string,
    { given?: boolean; value?: number; exact?: string[]; kind?: 'length' | 'free' | 'int' }
  >;
  givens?: string[];
  admitted?: string[];
}

export interface LegacyStatement {
  text: string;
  s: Statement;
}

export interface LegacySheetFacts {
  /** valeur EXACTE de chaque grandeur donnée (ou admise : « BC = 10 ») */
  known: Map<string, Term>;
  /** longueur → strictement positive ; rang → entier */
  hypotheses: Condition[];
  /** égalités de l'énoncé et résultats admis, lus */
  statements: LegacyStatement[];
  /** ce qui n'a pas pu être lu (une valeur exacte, une égalité de l'énoncé) */
  unread: string[];
  /**
   * grandeurs données par un flottant tronqué (2,9166666666666665 pour 35/12) : prises
   * telles quelles (valeur décimale exacte), mais aucune égalité exacte ne tombera juste
   */
  inexact: string[];
}

/**
 * Valeur numérique du modèle utilisable comme valeur EXACTE : un flottant qui n'est qu'un
 * arrondi (2,9166666666666665 pour 35/12, 1,4142135623730951 pour √2) ne l'est pas.
 */
export function isExactModelValue(v: number): boolean {
  return Number.isFinite(v) && (String(v).split('.')[1]?.length ?? 0) < INEXACT_DECIMALS;
}
const INEXACT_DECIMALS = 10;

function exactValue(spec: LegacyModelView['symbols'][string]): Term | null {
  if (spec.exact?.length) {
    const r = readLegacyLine(spec.exact[0]!);
    if (r.ok && r.parts.length === 1 && r.parts[0]!.k === 'expr') return r.parts[0]!.e;
    return null;
  }
  if (spec.value === undefined) return null;
  try {
    return fromNode({ k: 'n', v: spec.value });
  } catch {
    return null;
  }
}

/** Ce que la feuille sait d'emblée. */
export function legacySheetFacts(model: LegacyModelView): LegacySheetFacts {
  const known = new Map<string, Term>();
  const hypotheses: Condition[] = [];
  const statements: LegacyStatement[] = [];
  const unread: string[] = [];
  const inexact: string[] = [];
  for (const [name, spec] of Object.entries(model.symbols)) {
    if (spec.kind === 'length') hypotheses.push({ kind: 'positive', of: [name] });
    if (spec.kind === 'int') hypotheses.push({ kind: 'integer', of: [name] });
    if (!spec.given) continue;
    if (!spec.exact?.length && spec.value !== undefined && !isExactModelValue(spec.value))
      inexact.push(name);
    const v = exactValue(spec);
    if (v) known.set(name, v);
    else unread.push(name);
  }
  for (const text of [...(model.givens ?? []), ...(model.admitted ?? [])]) {
    const r = readLegacyLine(text);
    if (!r.ok) {
      unread.push(text);
      continue;
    }
    for (const s of r.parts) {
      statements.push({ text, s });
      const def = constantDefinition(s);
      if (def && !known.has(def.name)) known.set(def.name, def.value);
    }
  }
  return { known, hypotheses, statements, unread, inexact };
}
